import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, ReferenceLine, Legend,
} from "recharts";
import {
  ArrowLeft, User, Target, TrendingUp, Clock, BookOpen,
  MessageSquare, Sliders, Lightbulb, CheckCircle, AlertTriangle,
  GraduationCap, Layers, Brain, Send, Loader2, ExternalLink,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────
interface StudentData {
  student: { id: string; email: string; name: string | null; enrolledAt: string | null };
  summary: {
    progressPct: number; completedNodes: number; totalNodes: number;
    avgObjectiveMastery: number; avgExamScore: number; examPassRate: number;
    totalExamAttempts: number; passedExams: number;
    totalTimeMs: number; totalTimeMins: number;
    modalitiesUsed: string[]; tutorMessageCount: number; revisitCount: number;
    // Interaction event aggregates
    avgScrollDepthPct: number | null;
    scrolledBackCount: number;
    abandonmentCount: number;
    calibrationPoints: Array<{ confidence: number; score: number; passed: boolean; occurredAt: string }>;
    calibrationError: number | null;
    avgPreQuizConfidence: number | null;
    modalitySelectCounts: Record<string, number>;
    avgQuestionTimingMs: number | null;
  };
  nodeMastery: Array<{
    node_id: string; nodeTitle: string; isKeyNode: boolean;
    mastery_score: number; confidence_tier: string;
    exam_attempt_count: number; best_exam_score: number; last_exam_score: number;
    total_time_ms: number; tutor_message_count: number;
    modalities_used: string[]; revisit_count: number;
  }>;
  objectiveMastery: Array<{
    objective_id: string; objectiveTitle: string;
    mastery_score: number; confidence_tier: string;
    node_rollup_score: number; exam_objective_score: number;
    coverage_pct: number; nodes_mapped: number; nodes_with_signal: number;
  }>;
  examHistory: Array<{ id: string; score: number; passed: boolean; takenAt: string; nodeTitle: string }>;
  coachSuggestions: Array<{
    id: string; suggestion_type: string; message: string;
    priority: number; generated_at: string; is_acted_upon: boolean;
    cta_label: string | null; cta_url: string | null;
  }>;
  strugglingSessions: Array<{
    node_id: string; nodeTitle: string; mastery_score: number;
    exam?: { id: string; difficulty: string; is_mandatory: boolean; passing_score: number } | null;
  }>;
  aiAssessment: string;
  courseName: string;
  cohortAvgByObjective?: Record<string, number>;
  cohortAvgExamScore?: number | null;
  cohortExamPassRate?: number | null;
}

const TIER_COLOR: Record<string, string> = {
  none:     "hsl(var(--muted-foreground))",
  low:      "#ef4444",
  medium:   "#f59e0b",
  high:     "#22c55e",
  mastered: "#10b981",
};

const TIER_BADGE: Record<string, React.ReactNode> = {
  none:     <Badge variant="outline">Not assessed</Badge>,
  low:      <Badge className="bg-red-500">Beginner</Badge>,
  medium:   <Badge className="bg-amber-500">Developing</Badge>,
  high:     <Badge className="bg-green-500">Proficient</Badge>,
  mastered: <Badge className="bg-emerald-600">Mastered</Badge>,
};

function scoreTier(pct: number) {
  if (pct >= 85) return "mastered";
  if (pct >= 60) return "high";
  if (pct >= 35) return "medium";
  if (pct > 0)   return "low";
  return "none";
}

// ── Stat card ──────────────────────────────────────────────────────────────────
function Stat({ icon, label, value, sub, color = "text-primary" }: {
  icon: React.ReactNode; label: string; value: string | number;
  sub?: string; color?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg bg-muted ${color}`}>{icon}</div>
          <div>
            <p className="text-2xl font-bold leading-none mb-1">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Mastery bar ────────────────────────────────────────────────────────────────
function MasteryBar({ label, score, tier, sub }: {
  label: string; score: number; tier: string; sub?: string;
}) {
  const pct = Math.round(score * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center gap-2">
        <span className="text-sm font-medium truncate flex-1" title={label}>{label}</span>
        <span className="text-sm font-bold shrink-0" style={{ color: TIER_COLOR[tier] || TIER_COLOR.none }}>
          {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: TIER_COLOR[tier] || TIER_COLOR.none }}
        />
      </div>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StudentDetail() {
  const { id: courseId, studentId } = useParams<{ id: string; studentId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StudentData | null>(null);

  // Message dialog
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [sending, setSending] = useState(false);

  // Adjust difficulty dialog
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffChanges, setDiffChanges] = useState<Record<string, string>>({});
  const [savingDiff, setSavingDiff] = useState(false);

  // Generate suggestions
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);

  const load = useCallback(async () => {
    if (!courseId || !studentId) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth"); return; }

      const { data, error } = await invokeFunction("get-student-detail", {
        body: { courseId, studentId },
      });
      if (error) throw error;
      setData(data);
    } catch (e: any) {
      toast.error("Failed to load student data: " + (e?.message ?? "Unknown error"));
    } finally {
      setLoading(false);
    }
  }, [courseId, studentId, navigate]);

  useEffect(() => { load(); }, [load]);

  // ── Send message ──────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!msgBody.trim() || !data) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      const { error } = await supabase.from("instructor_messages").insert({
        instructor_id: session.user.id,
        student_id: studentId,
        course_id: courseId,
        subject: msgSubject.trim() || null,
        message: msgBody.trim(),
      });
      if (error) throw error;
      toast.success("Message sent to " + (data.student.name || data.student.email));
      setMsgOpen(false);
      setMsgSubject("");
      setMsgBody("");
    } catch (e: any) {
      toast.error("Failed to send: " + (e?.message ?? "Unknown error"));
    } finally {
      setSending(false);
    }
  };

  // ── Adjust difficulty ─────────────────────────────────────────────────────────
  const openDiffDialog = () => {
    if (!data) return;
    const initial: Record<string, string> = {};
    data.strugglingSessions.forEach(n => {
      if (n.exam) initial[n.exam.id] = n.exam.difficulty || "medium";
    });
    setDiffChanges(initial);
    setDiffOpen(true);
  };

  const saveDifficulty = async () => {
    setSavingDiff(true);
    try {
      await Promise.all(
        Object.entries(diffChanges).map(([examId, difficulty]) =>
          supabase.from("skill_node_exams").update({ difficulty, is_mandatory: false }).eq("id", examId)
        )
      );
      toast.success("Exam difficulties updated");
      setDiffOpen(false);
      load();
    } catch (e: any) {
      toast.error("Failed to update: " + (e?.message ?? "Unknown error"));
    } finally {
      setSavingDiff(false);
    }
  };

  // ── Generate suggestions ──────────────────────────────────────────────────────
  const generateSuggestions = async () => {
    if (!data) return;
    setGeneratingSuggestions(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      const { error } = await invokeFunction("generate-coach-suggestions", {
        body: {
          userId: studentId,
          courseId,
        },
      });
      if (error) throw error;
      toast.success("New coach suggestions generated for this student");
      load();
    } catch (e: any) {
      toast.error("Failed: " + (e?.message ?? "Unknown error"));
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return <LoadingSkeleton onBack={() => navigate(`/instructor/${courseId}`)} />;
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-muted-foreground mb-4">Failed to load student data.</p>
        <Button onClick={() => navigate(`/instructor/${courseId}`)}>Back to Dashboard</Button>
      </div>
    </div>
  );

  const { student, summary, nodeMastery, objectiveMastery, examHistory, coachSuggestions, strugglingSessions, aiAssessment, cohortAvgByObjective, cohortAvgExamScore } = data;
  const displayName = student.name || student.email;

  // Chart data
  const nodeChartData = [...nodeMastery]
    .sort((a, b) => a.mastery_score - b.mastery_score)
    .slice(0, 15)
    .map(n => ({
      name: n.nodeTitle.length > 22 ? n.nodeTitle.slice(0, 22) + "…" : n.nodeTitle,
      score: Math.round(n.mastery_score * 100),
      tier: n.confidence_tier || "none",
    }));

  const examChartData = examHistory.map((a, i) => ({
    index: i + 1,
    score: a.score,
    passed: a.passed,
    name: a.nodeTitle.length > 18 ? a.nodeTitle.slice(0, 18) + "…" : a.nodeTitle,
    date: new Date(a.takenAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }));

  const radarData = objectiveMastery.slice(0, 8).map(o => ({
    subject: o.objectiveTitle.length > 18 ? o.objectiveTitle.slice(0, 18) + "…" : o.objectiveTitle,
    score: Math.round(o.mastery_score * 100),
    cohort: cohortAvgByObjective?.[o.objective_id] ?? null,
    fullTitle: o.objectiveTitle,
  }));
  const hasRadarCohort = radarData.some(d => d.cohort !== null);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/instructor/${courseId}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">{displayName}</h1>
            <p className="text-xs text-muted-foreground">
              {data.courseName}
              {student.enrolledAt && ` · enrolled ${new Date(student.enrolledAt).toLocaleDateString()}`}
            </p>
          </div>
          {TIER_BADGE[scoreTier(summary.avgObjectiveMastery)] ?? null}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6 max-w-5xl">

        {/* ── Key stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={<TrendingUp className="h-4 w-4" />} label="Course Progress"
            value={`${summary.progressPct}%`}
            sub={`${summary.completedNodes}/${summary.totalNodes} nodes`}
            color="text-blue-500" />
          <Stat icon={<Target className="h-4 w-4" />} label="Objective Mastery"
            value={`${summary.avgObjectiveMastery}%`}
            color="text-purple-500" />
          <Stat icon={<GraduationCap className="h-4 w-4" />} label="Exam Pass Rate"
            value={`${summary.examPassRate}%`}
            sub={`${summary.passedExams}/${summary.totalExamAttempts} attempts`}
            color={summary.examPassRate >= 70 ? "text-green-500" : "text-red-500"} />
          <Stat icon={<Clock className="h-4 w-4" />} label="Time on Content"
            value={`${summary.totalTimeMins}m`}
            sub={`${summary.tutorMessageCount} tutor msgs`}
            color="text-amber-500" />
        </div>

        {/* ── Action bar ── */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setMsgOpen(true)} className="gap-2" variant="default">
            <MessageSquare className="h-4 w-4" /> Message Student
          </Button>
          <Button onClick={openDiffDialog} variant="outline" className="gap-2"
            disabled={strugglingSessions.filter(n => n.exam).length === 0}>
            <Sliders className="h-4 w-4" />
            Adjust Exam Difficulty
            {strugglingSessions.filter(n => n.exam).length > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">
                {strugglingSessions.filter(n => n.exam).length} at-risk
              </Badge>
            )}
          </Button>
          <Button onClick={generateSuggestions} variant="outline" className="gap-2"
            disabled={generatingSuggestions}>
            {generatingSuggestions
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Lightbulb className="h-4 w-4" />}
            Generate Coach Suggestions
          </Button>
          <Button variant="outline" className="gap-2" asChild>
            <a href={`/project/${courseId}`} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4" /> Edit Course
            </a>
          </Button>
        </div>

        {/* ── Tabs ── */}
        <Tabs defaultValue="assessment">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="assessment" className="gap-1.5">
              <Brain className="h-3.5 w-3.5" /> AI Assessment
            </TabsTrigger>
            <TabsTrigger value="mastery" className="gap-1.5">
              <Target className="h-3.5 w-3.5" /> Mastery
            </TabsTrigger>
            <TabsTrigger value="exams" className="gap-1.5">
              <GraduationCap className="h-3.5 w-3.5" /> Exams
            </TabsTrigger>
            <TabsTrigger value="engagement" className="gap-1.5">
              <BookOpen className="h-3.5 w-3.5" /> Engagement
            </TabsTrigger>
          </TabsList>

          {/* ── AI Assessment tab ── */}
          <TabsContent value="assessment" className="space-y-4 mt-4">
            {aiAssessment ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="h-4 w-4 text-primary" />
                    AI Assessment — {displayName}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{aiAssessment}</ReactMarkdown>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  <Brain className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Not enough data yet for an AI assessment. Check back after the student completes some content.</p>
                </CardContent>
              </Card>
            )}

            {/* Active coach suggestions */}
            {coachSuggestions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    Active Coach Suggestions ({coachSuggestions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {coachSuggestions.map(s => (
                    <div key={s.id} className="rounded-lg border p-3 space-y-1">
                      <div className="flex gap-2 items-center">
                        <Badge variant="outline" className="text-xs">{s.suggestion_type.replace(/_/g, " ")}</Badge>
                        {s.priority <= 1 && <Badge variant="destructive" className="text-xs">High priority</Badge>}
                        {s.is_acted_upon && <Badge variant="secondary" className="text-xs">Acted upon</Badge>}
                      </div>
                      <p className="text-sm">{s.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Struggling nodes callout */}
            {strugglingSessions.length > 0 && (
              <Card className="border-red-200 dark:border-red-900">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    Struggling Nodes ({strugglingSessions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {strugglingSessions.map(n => (
                    <div key={n.node_id} className="flex items-center justify-between gap-3 py-1">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{n.nodeTitle}</p>
                        <p className="text-xs text-muted-foreground">
                          {Math.round(n.mastery_score * 100)}% mastery
                          {n.exam && ` · exam: ${n.exam.difficulty ?? "default"}`}
                          {n.exam?.is_mandatory && " · locked"}
                        </p>
                      </div>
                      <div className="h-1.5 w-24 bg-muted rounded-full overflow-hidden shrink-0">
                        <div
                          className="h-full rounded-full bg-red-500"
                          style={{ width: `${Math.round(n.mastery_score * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Mastery tab ── */}
          <TabsContent value="mastery" className="space-y-6 mt-4">
            {/* Objectives radar */}
            {radarData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Objective Mastery</CardTitle>
                </CardHeader>
                <CardContent>
                  {radarData.length >= 3 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                        <Radar name="This student" dataKey="score" stroke="#1C3829" fill="#1C3829" fillOpacity={0.25} strokeWidth={2} />
                        {hasRadarCohort && (
                          <Radar name="Class avg" dataKey="cohort" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 2" />
                        )}
                        <Tooltip formatter={(v: number | null) => [v !== null ? `${v}%` : "—", ""]} />
                        {hasRadarCohort && <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />}
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="space-y-3">
                      {objectiveMastery.map(o => (
                        <MasteryBar
                          key={o.objective_id}
                          label={o.objectiveTitle}
                          score={o.mastery_score}
                          tier={o.confidence_tier || scoreTier(o.mastery_score * 100)}
                          sub={`Coverage: ${Math.round(o.coverage_pct * 100)}% · node rollup: ${Math.round(o.node_rollup_score * 100)}%`}
                        />
                      ))}
                    </div>
                  )}
                  {/* Objective list below chart */}
                  {radarData.length >= 3 && (
                    <div className="mt-4 space-y-2">
                      {objectiveMastery.map(o => (
                        <MasteryBar
                          key={o.objective_id}
                          label={o.objectiveTitle}
                          score={o.mastery_score}
                          tier={o.confidence_tier || scoreTier(o.mastery_score * 100)}
                          sub={`Coverage ${Math.round(o.coverage_pct * 100)}% · ${o.nodes_with_signal}/${o.nodes_mapped} nodes assessed`}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Node mastery chart */}
            {nodeChartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Node Mastery
                    {nodeMastery.length > 15 && <span className="text-sm font-normal text-muted-foreground ml-2">(lowest 15 shown)</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={Math.max(200, nodeChartData.length * 28)}>
                    <BarChart data={nodeChartData} layout="vertical" margin={{ left: 0, right: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => [`${v}%`, "Mastery"]} />
                      <ReferenceLine x={40} stroke="#ef4444" strokeDasharray="4 2" label={{ value: "40%", position: "top", fontSize: 10, fill: "#ef4444" }} />
                      <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                        {nodeChartData.map((entry, i) => (
                          <Cell key={i} fill={TIER_COLOR[entry.tier] || TIER_COLOR.none} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {nodeMastery.length === 0 && objectiveMastery.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  No mastery data yet — student hasn't completed any assessments.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Exams tab ── */}
          <TabsContent value="exams" className="space-y-4 mt-4">
            {summary.abandonmentCount > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 flex items-center gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <span>
                  <strong>{summary.abandonmentCount} exam{summary.abandonmentCount !== 1 ? "s" : ""}</strong> started but never submitted.
                  {summary.abandonmentCount >= 3 && " This may indicate exam anxiety or difficulty with the material."}
                </span>
              </div>
            )}
            {examChartData.length > 0 ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Exam Score Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={examChartData} margin={{ top: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v: number, _k: string, p: any) => [`${v}%`, p.payload.name]}
                          labelFormatter={l => `Attempt ${l}`}
                        />
                        <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="4 2" label={{ value: "Pass", position: "right", fontSize: 10, fill: "#f59e0b" }} />
                        {cohortAvgExamScore != null && (
                          <ReferenceLine y={cohortAvgExamScore} stroke="#6366f1" strokeDasharray="4 2" label={{ value: `Class avg ${cohortAvgExamScore}%`, position: "right", fontSize: 10, fill: "#6366f1" }} />
                        )}
                        <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                          {examChartData.map((entry, i) => (
                            <Cell key={i} fill={entry.passed ? "#22c55e" : "#ef4444"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-4 py-2 font-medium">Node</th>
                          <th className="text-left px-4 py-2 font-medium">Score</th>
                          <th className="text-left px-4 py-2 font-medium">Result</th>
                          <th className="text-left px-4 py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...examHistory].reverse().map(a => (
                          <tr key={a.id} className="border-b last:border-0 hover:bg-muted/30">
                            <td className="px-4 py-2 max-w-[200px] truncate">{a.nodeTitle}</td>
                            <td className="px-4 py-2 font-medium">{a.score}%</td>
                            <td className="px-4 py-2">
                              {a.passed
                                ? <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3.5 w-3.5" /> Passed</span>
                                : <span className="flex items-center gap-1 text-red-500"><AlertTriangle className="h-3.5 w-3.5" /> Failed</span>}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground text-xs">
                              {new Date(a.takenAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  No exam attempts recorded yet.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Engagement tab ── */}
          <TabsContent value="engagement" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={<Clock className="h-4 w-4" />} label="Total Time"
                value={`${summary.totalTimeMins}m`} color="text-blue-500" />
              <Stat icon={<Brain className="h-4 w-4" />} label="Tutor Messages"
                value={summary.tutorMessageCount} color="text-purple-500" />
              <Stat icon={<TrendingUp className="h-4 w-4" />} label="Node Revisits"
                value={summary.revisitCount} color="text-amber-500" />
              <Stat icon={<BookOpen className="h-4 w-4" />} label="Avg Scroll Depth"
                value={summary.avgScrollDepthPct !== null ? `${summary.avgScrollDepthPct}%` : "—"}
                sub="of lesson content"
                color={summary.avgScrollDepthPct !== null && summary.avgScrollDepthPct < 60 ? "text-red-500" : "text-green-500"} />
            </div>

            {/* Modalities used */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Learning Modalities Used</CardTitle>
              </CardHeader>
              <CardContent>
                {summary.modalitiesUsed.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {summary.modalitiesUsed.map(m => (
                      <Badge key={m} variant="secondary" className="gap-1.5 text-sm py-1 px-3">
                        {m === "tutor" ? <Brain className="h-3 w-3" />
                          : m === "slides" ? <Layers className="h-3 w-3" />
                          : m === "flashcards" ? <Layers className="h-3 w-3" />
                          : <BookOpen className="h-3 w-3" />}
                        {m}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No modality data recorded.</p>
                )}
              </CardContent>
            </Card>

            {/* Exam behaviour signals */}
            {(summary.abandonmentCount > 0 || summary.avgQuestionTimingMs !== null || summary.calibrationError !== null) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-primary" />
                    Exam Behaviour Signals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Abandoned Exams</p>
                      <p className="text-2xl font-bold">{summary.abandonmentCount}</p>
                      <p className="text-xs text-muted-foreground">started but never submitted</p>
                    </div>
                    {summary.avgQuestionTimingMs !== null && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Avg Time / Question</p>
                        <p className="text-2xl font-bold">{Math.round(summary.avgQuestionTimingMs / 1000)}s</p>
                        <p className="text-xs text-muted-foreground">across all exam questions</p>
                      </div>
                    )}
                    {summary.calibrationError !== null && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Calibration Error</p>
                        <p className={`text-2xl font-bold ${summary.calibrationError > 30 ? "text-red-500" : summary.calibrationError > 15 ? "text-amber-500" : "text-green-600"}`}>
                          {summary.calibrationError}pts
                        </p>
                        <p className="text-xs text-muted-foreground">
                          avg confidence {summary.avgPreQuizConfidence}/5 · 0 = perfectly calibrated
                        </p>
                      </div>
                    )}
                  </div>
                  {summary.calibrationPoints.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Confidence vs. Actual Score (per attempt)</p>
                      <div className="space-y-1.5">
                        {summary.calibrationPoints.slice(-10).map((p, i) => {
                          const expectedPct = (p.confidence - 1) * 25;
                          const diff = p.score - expectedPct;
                          return (
                            <div key={i} className="flex items-center gap-3 text-sm">
                              <span className="text-muted-foreground w-20 shrink-0 text-xs">
                                {new Date(p.occurredAt).toLocaleDateString()}
                              </span>
                              <span className="shrink-0">Conf {p.confidence}/5</span>
                              <span className="shrink-0">→</span>
                              <span className={`font-medium shrink-0 ${p.passed ? "text-green-600" : "text-red-500"}`}>
                                {p.score}% {p.passed ? "✓" : "✗"}
                              </span>
                              <span className={`text-xs shrink-0 ${diff > 10 ? "text-green-600" : diff < -15 ? "text-red-500" : "text-muted-foreground"}`}>
                                {diff > 0 ? `+${diff}` : diff}pts vs expected
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Modality selection frequency */}
            {Object.keys(summary.modalitySelectCounts).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Mode Selection Frequency</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">How many times the student chose each study mode</p>
                  <div className="space-y-2">
                    {Object.entries(summary.modalitySelectCounts)
                      .sort(([, a], [, b]) => b - a)
                      .map(([mode, count]) => {
                        const total = Object.values(summary.modalitySelectCounts).reduce((s, v) => s + v, 0);
                        return (
                          <div key={mode} className="flex items-center gap-3">
                            <span className="text-sm capitalize w-24 shrink-0">{mode}</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${Math.round((count / total) * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-node engagement details */}
            {nodeMastery.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Per-Node Engagement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...nodeMastery]
                      .sort((a, b) => (b.total_time_ms ?? 0) - (a.total_time_ms ?? 0))
                      .slice(0, 12)
                      .map(n => (
                        <div key={n.node_id} className="flex items-center gap-4 py-1 border-b last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{n.nodeTitle}</p>
                            <p className="text-xs text-muted-foreground">
                              {Math.round((n.total_time_ms ?? 0) / 60000)}m spent
                              {n.tutor_message_count > 0 && ` · ${n.tutor_message_count} tutor msgs`}
                              {n.revisit_count > 0 && ` · ${n.revisit_count} revisits`}
                            </p>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {(n.modalities_used ?? []).map(m => (
                              <span key={m} className="text-xs bg-muted px-1.5 py-0.5 rounded">{m}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* ── Message dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Message {displayName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Subject (optional)"
              value={msgSubject}
              onChange={e => setMsgSubject(e.target.value)}
            />
            <Textarea
              placeholder="Write your message to the student…"
              value={msgBody}
              onChange={e => setMsgBody(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              The student will see this message in their learning dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)}>Cancel</Button>
            <Button onClick={sendMessage} disabled={!msgBody.trim() || sending} className="gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Adjust difficulty dialog ──────────────────────────────────────────── */}
      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sliders className="h-4 w-4" />
              Adjust Exam Difficulty for Struggling Nodes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Lowering the difficulty makes exams easier and unlocks the node for the student.
              The mandatory lock will be cleared so they can customise further.
            </p>
            {strugglingSessions.filter(n => n.exam).map(n => (
              <div key={n.node_id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{n.nodeTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    Mastery: {Math.round(n.mastery_score * 100)}%
                    {n.exam?.is_mandatory && " · currently locked"}
                  </p>
                </div>
                <Select
                  value={diffChanges[n.exam!.id] || n.exam!.difficulty || "medium"}
                  onValueChange={v => setDiffChanges(prev => ({ ...prev, [n.exam!.id]: v }))}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)}>Cancel</Button>
            <Button onClick={saveDifficulty} disabled={savingDiff} className="gap-2">
              {savingDiff ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function LoadingSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background py-3 px-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <Skeleton className="h-5 w-40 mb-1" />
          <Skeleton className="h-3 w-28" />
        </div>
      </header>
      <main className="container mx-auto px-4 py-6 space-y-6 max-w-5xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </main>
    </div>
  );
}
